/** Entry editor (Figma edit frame) for folder entries and singletons, with translations, history and publishing. */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ConflictError,
  NEW_SLUG,
  basename,
  entryMedia,
  mergeData,
  deepEqual,
  mediaFor,
  mediaUploadPath,
  mediaValue,
  renderTemplate,
  resolveMediaPath,
  validateFields,
  type Data,
  type Entry,
  type Field,
  type FolderCollection,
  type MediaConfig,
  type Problem,
  type SingletonFile,
  type Upload,
} from '@hotpot/core';
import { Layout } from '../components/Layout.js';
import { Dialog, ErrorBox, Loading, paths, Status, useConfirm } from '../components/ui.js';
import { EditorContext, type EditorCtx } from '../fields/context.js';
import { FieldList, problemText } from '../fields/Fields.js';
import { LOCALE_NAMES, useT } from '../i18n.js';
import { joinUrl } from '../lib/format.js';
import { prepareImage, uniqueName } from '../lib/images.js';
import { Link, useRouter } from '../router.js';
import { useBackend, useSession, useSite, useToast } from '../state.js';
import { errorText } from './Login.js';
import { MediaPicker } from './Media.js';
import { PreviewLink, useDraftActions } from '../components/Drafts.js';
import { LivePreview } from '../components/LivePreview.js';
import { EntryHistory } from './History.js';

interface Props {
  collection: string;
  slug?: string;
  file?: string;
  isNew?: boolean;
  from?: string;
}

interface Loaded {
  /** One entry per locale file (multiple_files) or a single entry under the default locale. */
  entries: Record<string, Entry>;
  data: Record<string, Data>;
}

export function EntryEditor(props: Props) {
  const { t, lang } = useT();
  const site = useSite();
  const { me } = useSession();
  const backend = useBackend();
  const toast = useToast();
  const { navigate, setGuard } = useRouter();
  const [confirm, confirmDialog] = useConfirm();
  const { content, config } = site;
  const id = site.handle.site.id;
  const coll = config.collections.find((c) => c.name === props.collection);
  const singleton: SingletonFile | undefined = coll?.type === 'files' ? coll.files.find((f) => f.name === props.file) : undefined;
  const folder = coll?.type === 'folder' ? (coll as FolderCollection) : undefined;
  const fields: Field[] = singleton?.fields ?? folder?.fields ?? [];
  const i18n = config.i18n;
  const defaultLocale = i18n?.default ?? 'cs';
  const perLocaleFiles = !!(singleton && i18n?.structure === 'multiple_files' && singleton.file.includes('{{locale}}'));
  const i18nMode: EditorCtx['i18nMode'] = !i18n ? 'none' : perLocaleFiles ? 'multiple_files' : i18n.structure === 'field_suffix' ? 'field_suffix' : 'none';
  const hasTranslations = i18nMode === 'multiple_files' || (i18nMode === 'field_suffix' && JSON.stringify(fields).includes('"translations"'));
  const locales = hasTranslations ? i18n!.locales : [defaultLocale];
  // No language tabs: main text and translation side by side, both editable (i18n.layout in hotpot-cms.yaml).
  const sideBySide = i18nMode === 'field_suffix' && hasTranslations && i18n?.layout === 'side_by_side';

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [locale, setLocale] = useState(defaultLocale);
  // The entry (and its data) of the language being edited. With `field_suffix` translations (`title_en` next to `title`)
  // there is one entry in the default language whatever tab is open, so edits in the EN tab belong to it too.
  const dataLocale = i18nMode === 'multiple_files' ? locale : defaultLocale;
  const [uploads, setUploads] = useState<Upload[]>([]);
  const previews = useRef(new Map<string, string>());
  const [problems, setProblems] = useState<Problem[]>([]);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  /** A save hit someone else's newer version and both changed the same fields: the editor decides. */
  const [conflict, setConflict] = useState<null | { fields: string[]; next: Loaded; publish: boolean }>(null);
  const [picker, setPicker] = useState<{ media: MediaConfig; multiple: boolean; resolve: (v: string[] | null) => void } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  /** Live preview beside the form (remembered per browser), and what the page in it was built from. */
  const [liveOn, setLiveOn] = useState(() => {
    try {
      return localStorage.getItem('hp-live') !== 'off';
    } catch {
      return true;
    }
  });
  const [shown, setShown] = useState<{ url: string; base: Data; key: number } | null>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const [headHeight, setHeadHeight] = useState(120);
  /** Other people who have this entry open right now. */
  const [others, setOthers] = useState<{ name: string; email: string }[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  // Media "next to the entry" (empty folder) becomes the entry's own folder.
  const media = (field?: { media?: MediaConfig }) => entryMedia(mediaFor(config, coll, singleton, field), entryPath);

  // ------------------------------------------------------------ load
  useEffect(() => {
    let alive = true;
    setLoaded(null);
    setError(null);
    setUploads([]);
    setProblems([]);
    setTried(false);
    setLocale(defaultLocale);
    (async () => {
      const entries: Record<string, Entry> = {};
      if (singleton) {
        for (const l of perLocaleFiles ? i18n!.locales : [undefined]) {
          entries[l ?? defaultLocale] = await content.readSingleton(props.collection, singleton.name, l);
        }
      } else if (props.isNew) {
        const entry = content.newEntry(props.collection);
        if (props.from) {
          const src = await content.readEntry(props.collection, props.from);
          entry.data = content.duplicateData(props.collection, src.data, t('duplicateSuffix'));
        }
        entries[defaultLocale] = entry;
      } else {
        entries[defaultLocale] = await content.readEntry(props.collection, props.slug!);
      }
      const data: Record<string, Data> = {};
      for (const [l, e] of Object.entries(entries)) data[l] = structuredClone(e.data);
      if (alive) setLoaded({ entries, data });
    })().catch((e) => alive && setError(e));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.collection, props.slug, props.file, props.isNew, props.from, reloadKey]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    if (uploads.length) return true;
    if (props.isNew) return true;
    return Object.keys(loaded.data).some((l) => !deepEqual(loaded.data[l], loaded.entries[l]!.data));
  }, [loaded, uploads, props.isNew]);

  // ------------------------------------------------------------ leave guard
  const pendingNav = useRef<string | null>(null);
  const [leaveAsk, setLeaveAsk] = useState(false);
  useEffect(() => {
    if (!dirty) {
      setGuard(null);
      return;
    }
    setGuard((to) => {
      pendingNav.current = to;
      setLeaveAsk(true);
      return false;
    });
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => {
      setGuard(null);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [dirty, setGuard]);

  // ------------------------------------------------------------ editor context
  const entryPath = loaded?.entries[dataLocale]?.path || (folder ? content.entryPath(folder, NEW_SLUG) : singleton?.file ?? '');

  const upload = useCallback(
    async (files: File[], field: { media?: MediaConfig }) => {
      const m = media(field);
      const existing = new Set((await site.handle.storage.list(m.folder)).map((f) => basename(f.path)));
      for (const u of uploads) existing.add(basename(u.path));
      const values: string[] = [];
      const added: Upload[] = [];
      for (const file of files) {
        const img = await prepareImage(file, config.uploads);
        const name = uniqueName(img.name, existing);
        existing.add(name);
        const value = mediaValue(name, m);
        previews.current.set(value, img.previewUrl);
        added.push({ path: mediaUploadPath(name, m), base64: img.base64 });
        values.push(value);
      }
      setUploads((u) => [...u, ...added]);
      return values;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploads, config, coll, singleton, site.handle.storage],
  );

  const ctx: EditorCtx = {
    entryPath,
    defaultLocale,
    locale,
    i18nMode,
    reference: i18nMode === 'multiple_files' && locale !== defaultLocale ? loaded?.data[defaultLocale] : undefined,
    mediaFor: media,
    upload,
    imageSrc: (value, field) => {
      const p = previews.current.get(value);
      if (p) return p;
      const path = resolveMediaPath(value, entryPath, media(field));
      return path ? site.handle.storage.mediaUrl(path) : value;
    },
    pick: (field, multiple) => new Promise((resolve) => setPicker({ media: media(field), multiple, resolve })),
    problems,
    showAdvanced: false,
    sideBySide,
    autoTranslate: sideBySide && !!i18n?.auto_translate,
  };

  // ------------------------------------------------------------ validation + save
  const validate = (state: Loaded): Problem[] => {
    const out: Problem[] = [];
    for (const l of Object.keys(state.data)) {
      // Only the main language has required fields; translations may stay empty (they fall back).
      if (l !== defaultLocale && i18nMode !== 'multiple_files') continue;
      const list = validateFields(
        fields.filter((f) => !(folder?.draft_field === f.name)),
        state.data[l]!,
      );
      out.push(...(l === locale || l === defaultLocale ? list : []));
    }
    return out;
  };

  useEffect(() => {
    if (tried && loaded) setProblems(validate(loaded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded?.data, tried]);

  const drafts = site.handle.drafts;
  const draftActions = useDraftActions();
  const viewPath = (data: Data, slug?: string) => {
    const tpl = singleton?.preview_path ?? folder?.preview_path;
    return tpl ? renderTemplate(tpl, data, { slug: slug ?? '' }) : undefined;
  };
  const viewUrl = (data: Data, slug?: string) => {
    const path = viewPath(data, slug);
    if (path === undefined || !site.handle.site.url) return undefined;
    return joinUrl(site.handle.site.url, path);
  };

  /** Save; on a site with drafts the save is a draft, and `publish` also puts every draft on the live site. */
  const save = async (publish = !drafts, state: Loaded | null = loaded, retried = false): Promise<void> => {
    if (!state) return;
    const loaded = state;
    setTried(true);
    const found = validate(loaded);
    setProblems(found);
    if (found.length) {
      toast(t('fixErrors'));
      const first = found[0]!.path;
      requestAnimationFrame(() => {
        const el = document.getElementById(`f-${first}`) ?? document.querySelector(`[id^="f-${first}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el?.querySelector('input, textarea, select, [contenteditable]') as HTMLElement | null)?.focus({ preventScroll: true });
      });
      return;
    }
    setSaving(true);
    try {
      const items = Object.keys(loaded.data)
        .filter((l) => !loaded.entries[l]!.sha || !deepEqual(loaded.data[l], loaded.entries[l]!.data) || props.isNew)
        .map((l) => ({ entry: loaded.entries[l]!, data: loaded.data[l]! }));
      if (!items.length && uploads.length) items.push({ entry: loaded.entries[defaultLocale]!, data: loaded.data[defaultLocale]! });
      const { commit, entries } = await content.saveMany(items, site.author, uploads, { skipPreview: !!drafts && publish });
      const saved = entries.find((e) => (e.locale ?? defaultLocale) === defaultLocale) ?? entries[0]!;
      const isDraft = folder?.draft_field ? saved.data[folder.draft_field] === true : false;
      const url = isDraft ? undefined : viewUrl(saved.data, saved.slug);
      site.afterCommit(commit, url);
      if (!drafts) toast(t('savedPublishing'));
      else if (!publish) toast(t('savedDraft'));
      else await draftActions.publish({ confirm: false, url });
      const nextEntries = { ...loaded.entries };
      const nextData = { ...loaded.data };
      for (const e of entries) {
        nextEntries[e.locale ?? defaultLocale] = e;
        nextData[e.locale ?? defaultLocale] = structuredClone(e.data);
      }
      setLoaded({ entries: nextEntries, data: nextData });
      setUploads([]);
      setTried(false);
      setProblems([]);
      if (props.isNew && saved.slug) {
        setGuard(null);
        navigate(paths.entry(id, props.collection, saved.slug), { replace: true, force: true });
      }
    } catch (e) {
      if (!(e instanceof ConflictError)) toast(errorText(e, t));
      else if (props.isNew || retried) setConflict({ fields: [], next: loaded, publish });
      else {
        // Someone saved this entry after it was opened: combine their changes with ours, field by field.
        const merged = await mergeWithLatest(loaded).catch(() => null);
        if (!merged) setConflict({ fields: [], next: loaded, publish });
        else if (merged.conflicts.length) {
          setLoaded(merged.next);
          setConflict({ fields: merged.conflicts, next: merged.next, publish });
        } else {
          setLoaded(merged.next);
          toast(t('mergedOthers'));
          setSaving(false);
          return save(publish, merged.next, true);
        }
      }
    } finally {
      setSaving(false);
    }
  };

  // Tell the server this entry is open (every 20 s while the tab is visible) and learn who else has it open.
  const presencePath = loaded?.entries[defaultLocale]?.sha ? loaded.entries[defaultLocale]!.path : '';
  useEffect(() => {
    const ping = site.handle.presence;
    if (!presencePath || !ping) return;
    let alive = true;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      ping(presencePath).then((list) => alive && setOthers(list), () => {});
    };
    tick();
    const timer = setInterval(tick, 20_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      alive = false;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      setOthers([]);
      void ping(presencePath, true).catch(() => {});
    };
  }, [presencePath, site.handle]);

  // The page the live preview shows: the preview site while there are unpublished changes, else the live site.
  const livePageUrl = () => {
    const saved = loaded?.entries[defaultLocale];
    if (!saved?.sha) return '';
    const root = drafts && site.pending?.files.length && drafts.previewUrl ? drafts.previewUrl : site.handle.site.url;
    const path = viewPath(saved.data, saved.slug);
    return root && path !== undefined ? joinUrl(root, path) : '';
  };
  const savedData = loaded?.entries[defaultLocale]?.data;
  useEffect(() => {
    if (!shown && savedData && loaded?.entries[defaultLocale]?.sha) setShown({ url: livePageUrl(), base: savedData, key: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedData]);
  // When a new build is live, show it and measure changes from what was saved.
  const builtState = `${site.preview?.sha}:${site.preview?.status.state}:${site.publish?.sha}:${site.publish?.status.state}`;
  useEffect(() => {
    // The demo's page copy never gets rebuilt: it keeps showing the changes against the page as it was.
    if (!shown || !savedData || backend.demo) return;
    const live = site.preview?.status.state === 'live' || site.publish?.status.state === 'live';
    if (live && JSON.stringify(shown.base) !== JSON.stringify(savedData)) setShown({ url: livePageUrl(), base: savedData, key: shown.key + 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [builtState]);
  const building = [site.preview, site.publish].some((b) => b && b.status.state !== 'live' && b.status.state !== 'failed');
  useLayoutEffect(() => {
    const el = headRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setHeadHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  });
  // The form, for the live preview to scroll along with it.
  const formRef = useRef<HTMLDivElement>(null);

  const toggleLive = () =>
    setLiveOn((on) => {
      try {
        localStorage.setItem('hp-live', on ? 'off' : 'on');
      } catch {
        /* storage blocked */
      }
      return !on;
    });

  /** The latest saved version of each file, with our changes applied on top where they don't collide. */
  const mergeWithLatest = async (state: Loaded) => {
    const next: Loaded = { entries: { ...state.entries }, data: { ...state.data } };
    const conflicts = new Set<string>();
    for (const l of Object.keys(state.entries)) {
      const latest = singleton
        ? await content.readSingleton(props.collection, singleton.name, perLocaleFiles ? l : undefined)
        : await content.readEntry(props.collection, props.slug!);
      const r = mergeData(state.entries[l]!.data, state.data[l]!, latest.data);
      next.entries[l] = latest;
      next.data[l] = r.data;
      r.conflicts.forEach((f) => conflicts.add(f));
    }
    return { next, conflicts: [...conflicts] };
  };

  // Cmd/Ctrl+S saves.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        // On sites with drafts, the shortcut saves a draft (publishing is always a deliberate click).
        void saveRef.current(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const remove = async () => {
    if (!folder || !loaded || !props.slug) return;
    const title = content.titleOf({ collection: folder.name, slug: props.slug }, loaded.data[defaultLocale]!);
    if (!(await confirm({ title: t('deleteEntryTitle', { name: title }), text: t('deleteEntryText'), ok: t('deleteForever') }))) return;
    try {
      const commit = await content.deleteEntries(folder.name, [{ slug: props.slug, sha: loaded.entries[defaultLocale]!.sha ?? undefined }], site.author);
      site.afterCommit(commit, site.handle.site.url || undefined);
      setGuard(null);
      toast(t('deleted'));
      navigate(paths.list(id, folder.name), { force: true });
    } catch (e) {
      toast(e instanceof ConflictError ? t('conflictTitle') : errorText(e, t));
    }
  };

  // ------------------------------------------------------------ render
  if (!coll || (!folder && !singleton)) return null;
  const backTo = folder ? paths.list(id, folder.name) : config.settings?.includes(`${coll.name}/${singleton!.name}`) ? `/s/${id}/settings` : paths.site(id);
  const backLabel = folder ? folder.label : config.settings?.includes(`${coll.name}/${singleton!.name}`) ? t('globalSettings') : site.handle.site.name;

  if (error) {
    return (
      <Layout>
        <Link className="crumb" to={backTo}>
          ← {backLabel}
        </Link>
        <ErrorBox error={error} onRetry={() => setReloadKey((k) => k + 1)} />
      </Layout>
    );
  }
  if (!loaded) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  const data = loaded.data[dataLocale] ?? loaded.data[defaultLocale]!;
  const setData = (next: Data) => setLoaded((s) => (s ? { ...s, data: { ...s.data, [dataLocale]: next } } : s));
  const mainData = loaded.data[defaultLocale]!;
  const title = singleton
    ? singleton.label
    : props.isNew && !mainData[folder!.title_field]
      ? t('newEntry', { name: folder!.label_singular ?? folder!.label })
      : String(mainData[folder!.title_field] ?? '') || t('untitled');
  const draftField = folder?.draft_field;
  const isDraft = draftField ? mainData[draftField] === true : false;
  const savedEntry = loaded.entries[defaultLocale]!;
  const url = savedEntry.sha && !isDraft ? viewUrl(savedEntry.data, savedEntry.slug) : undefined;
  const publish = site.publish;
  const localeName = (l: string) => LOCALE_NAMES[l]?.[lang === 'cs' ? 'cs' : 'en'] ?? l;
  const rootList = fields.find((f) => f.type === 'list' && f.root);

  return (
    <Layout>
      <EditorContext.Provider value={ctx}>
        <div className="editor-head" ref={headRef}>
          <Link className="crumb" to={backTo}>
            ← {backLabel}
          </Link>
          <div className="title-row">
            <div style={{ minWidth: 0 }}>
              <h1 style={{ overflowWrap: 'anywhere' }}>{title}</h1>
              {singleton?.description && <p className="desc">{singleton.description}</p>}
            </div>
            <div className="actions">
              {folder && savedEntry.sha && folder.delete !== false && (
                <button className="link-btn danger" onClick={remove}>
                  {t('delete')}
                </button>
              )}
              {drafts ? (
                <>
                  <button className="link-btn draft" onClick={() => save(false)} disabled={saving || draftActions.busy || (!dirty && !props.isNew)}>
                    {t('saveDraft')}
                  </button>
                  {dirty || props.isNew || !site.pending?.files.length ? (
                    <button className="btn" onClick={() => save(true)} disabled={saving || draftActions.busy || (!dirty && !props.isNew)}>
                      {saving || draftActions.busy ? t('saving') : t('savePublish')}
                    </button>
                  ) : (
                    <button className="btn" onClick={() => draftActions.publish({ url })} disabled={draftActions.busy}>
                      {draftActions.busy ? t('publishPending') : t('publishAll')}
                    </button>
                  )}
                </>
              ) : (
                <button className="btn" onClick={() => save()} disabled={saving || (!dirty && !props.isNew)}>
                  {saving ? t('saving') : t('save')}
                </button>
              )}
            </div>
          </div>
          <div className="sub">
            {locales.length > 1 && !sideBySide && (
              <div className="lang-tabs" role="tablist" aria-label="Language">
                {locales.map((l) => (
                  <button key={l} role="tab" aria-selected={l === locale} className={l === locale ? 'on' : ''} onClick={() => setLocale(l)} title={localeName(l)}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            {draftField && (
              <label className="switch">
                <input type="checkbox" className="check" style={{ width: 24, height: 24, borderRadius: 8 }} checked={!isDraft} onChange={(e) => setLoaded((s) => (s ? { ...s, data: { ...s.data, [defaultLocale]: { ...s.data[defaultLocale], [draftField]: !e.target.checked } } } : s))} />
                {isDraft ? t('hiddenOnSite') : t('visibleOnSite')}
              </label>
            )}
            {dirty && !props.isNew && <span style={{ color: 'var(--accent-ink)' }}>● {t('unsaved')}</span>}
            {!dirty && publish && savedEntry.sha && (!drafts || publish.status.state !== 'live') && <Status status={publish.status} small url={publish.url} />}
            {drafts && savedEntry.sha && <PreviewLink path={viewPath(savedEntry.data, savedEntry.slug) ?? ''} />}
            {url && (
              <a className="link-btn" href={url} target="_blank" rel="noopener noreferrer">
                {t('viewOnSite')}
              </a>
            )}
            {folder && savedEntry.sha && folder.create !== false && (
              <Link className="link-btn" to={paths.newEntry(id, folder.name, props.slug)}>
                {t('duplicate')}
              </Link>
            )}
            {savedEntry.sha && (
              <button className="link-btn" onClick={() => setShowHistory(true)}>
                {t('history')}
              </button>
            )}
            {shown?.url && (
              <button className="link-btn lp-toggle" onClick={toggleLive} aria-pressed={liveOn}>
                {liveOn ? t('hideLivePreview') : t('showLivePreview')}
              </button>
            )}
          </div>
        </div>
        <div className={liveOn && shown?.url ? 'editor-split' : undefined} style={{ ['--edhead' as string]: `${headHeight}px` }}>
        {liveOn && shown?.url && (
          <LivePreview
            url={shown.url}
            fields={fields}
            base={shown.base}
            current={loaded.data[defaultLocale]!}
            imageSrc={(v) => ctx.imageSrc(v)}
            reloadKey={shown.key}
            waiting={building}
            demo={backend.demo}
            form={formRef}
            scrollSync={(folder ?? singleton)?.preview_scroll !== false}
          />
        )}
        <div className="editor-form" ref={formRef}>

        {others.length > 0 && (
          <p className="notice pink" role="status" style={{ marginBottom: 24 }}>
            {t('othersEditing', { names: others.map((o) => o.name).join(', ') })}
          </p>
        )}
        {props.from && <p className="notice" style={{ marginBottom: 24 }}>{t('duplicateNote')}</p>}
        {locale !== defaultLocale && i18nMode === 'field_suffix' && <p className="hint" style={{ margin: '-8px 0 20px' }}>{t('sameInAllLanguages')}</p>}
        {tried && problems.length > 0 && (
          <div className="notice pink" role="alert" style={{ marginBottom: 24 }}>
            {t('fixErrors')}
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {problems.slice(0, 6).map((p, i) => (
                <li key={i}>
                  <a href={`#f-${p.path}`} onClick={(e) => { e.preventDefault(); document.getElementById(`f-${p.path}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
                    {labelForPath(fields, p.path)}
                  </a>
                  : {problemText(p, t)}
                </li>
              ))}
            </ul>
          </div>
        )}

        <FieldList
          fields={rootList ? [rootList] : fields}
          parent={data}
          onParent={setData}
          path=""
          skip={draftField ? [draftField] : []}
          top
        />
        </div>
        </div>
      </EditorContext.Provider>

      {picker && (
        <MediaPicker
          media={picker.media}
          multiple={picker.multiple}
          onClose={(v) => {
            picker.resolve(v);
            setPicker(null);
          }}
        />
      )}
      {showHistory && (
        <EntryHistory
          path={savedEntry.path}
          fields={fields}
          format={singleton?.format ?? folder?.format ?? 'yaml'}
          current={savedEntry.data}
          label={title}
          onClose={() => setShowHistory(false)}
          onRestored={() => {
            setShowHistory(false);
            setReloadKey((k) => k + 1);
          }}
        />
      )}
      {conflict && (
        <Dialog
          title={t('conflictTitle')}
          onClose={() => setConflict(null)}
          buttons={
            conflict.fields.length ? (
              <>
                <button
                  className="link-btn"
                  onClick={() => {
                    setConflict(null);
                    setGuard(null);
                    setReloadKey((k) => k + 1);
                  }}
                >
                  {t('takeTheirs')}
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    const { next, publish } = conflict;
                    setConflict(null);
                    void save(publish, next, true);
                  }}
                >
                  {t('keepMine')}
                </button>
              </>
            ) : (
              <>
                <button className="link-btn" onClick={() => setConflict(null)}>
                  {t('close')}
                </button>
                <button
                  className="btn"
                  onClick={() => {
                    setConflict(null);
                    setGuard(null);
                    setReloadKey((k) => k + 1);
                  }}
                >
                  {t('reload')}
                </button>
              </>
            )
          }
        >
          <p style={{ margin: 0, lineHeight: 1.35 }}>
            {conflict.fields.length
              ? t('conflictFields', { fields: conflict.fields.map((f) => fields.find((x) => x.name === f)?.label ?? f).join(', ') })
              : t('conflictText')}
          </p>
        </Dialog>
      )}
      {leaveAsk && (
        <Dialog
          title={t('unsavedTitle')}
          onClose={() => setLeaveAsk(false)}
          buttons={
            <>
              <button
                className="link-btn danger"
                onClick={() => {
                  setLeaveAsk(false);
                  setGuard(null);
                  if (pendingNav.current) navigate(pendingNav.current, { force: true });
                }}
              >
                {t('leave')}
              </button>
              <button className="btn" onClick={() => setLeaveAsk(false)}>
                {t('stay')}
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>{t('unsavedText')}</p>
        </Dialog>
      )}
      {confirmDialog}
      {draftActions.dialogs}
      {savedEntry.sha && me?.user.studio && <p className="hint" style={{ marginTop: 30 }}><code>{savedEntry.path}</code></p>}
    </Layout>
  );
}

function labelForPath(fields: Field[], path: string): string {
  const parts = path.split('.');
  const labels: string[] = [];
  let list: Field[] | undefined = fields;
  for (const part of parts) {
    if (/^\d+$/.test(part)) {
      labels.push(String(Number(part) + 1));
      continue;
    }
    const f: Field | undefined = list?.find((x) => x.name === part);
    if (!f) break;
    labels.push(f.label);
    list = f.type === 'object' || f.type === 'list' ? (f as { fields?: Field[] }).fields : undefined;
  }
  return labels.join(' › ');
}
